class SequenceOverflowError extends Error {
    constructor(format, maxCombinations) {
        super(
            `Sequence exhausted for format '${format}'. Maximum possible combinations (${maxCombinations}) reached.`,
        );
        this.name = "SequenceOverflowError";
        this.format = format;
        this.maxCombinations = maxCombinations;
    }
}

export class SequenceGenerator {
    constructor(pattern, options = {}) {
        this.pattern = pattern;
        this.sequences = new Map();
        this.contextStates = new Map();
        this.options = {
            overflowStrategy: "error", // 'error', 'wrap', 'extend'
            ...options,
        };
        this.parsePattern();
    }

    getContextKey(sequenceKey, metadata) {
        const state = this.sequences.get(sequenceKey);
        if (!state.context.length) return 'default';

        return state.context
            .map(key => `${key}:${metadata[key]}`)
            .join('|');
    }

    parsePattern() {
        this.tokens = [];
        let currentPos = 0;

        while (currentPos < this.pattern.length) {
            const char = this.pattern[currentPos];

            if (char === "{") {
                const endPos = this.pattern.indexOf("}", currentPos);
                if (endPos === -1)
                    throw new Error("Invalid pattern: Unclosed curly brace");
                this.tokens.push({
                    type: "metadata",
                    key: this.pattern.slice(currentPos + 1, endPos),
                });
                currentPos = endPos + 1;
            } else if (char === "[") {
                const endPos = this.pattern.indexOf("]", currentPos);
                if (endPos === -1)
                    throw new Error("Invalid pattern: Unclosed square bracket");
                this.tokens.push({
                    type: "fixed",
                    value: this.pattern.slice(currentPos + 1, endPos),
                });
                currentPos = endPos + 1;
            } else if (char === "(") {
                const endPos = this.pattern.indexOf(")", currentPos);
                if (endPos === -1)
                    throw new Error("Invalid pattern: Unclosed parenthesis");

                let format = this.pattern.slice(currentPos + 1, endPos);
                let context = [];
                let initialValue = null;

                // Parse context and initial value if present (e.g., "bay,level:ccnn=AB05")
                if (format.includes(':')) {
                    let [contextPart, remainingPart] = format.split(':');
                    context = contextPart.split(',').map(c => c.trim());

                    if (remainingPart.includes('=')) {
                        const [formatPart, initialValuePart] = remainingPart.split('=');
                        format = formatPart;
                        initialValue = initialValuePart;
                    } else {
                        format = remainingPart;
                    }
                } else if (format.includes('=')) {
                    const [formatPart, initialValuePart] = format.split('=');
                    format = formatPart;
                    initialValue = initialValuePart;
                }

                const sequenceKey = `seq_${currentPos}`;
                this.initializeSequence(sequenceKey, format, context, initialValue);
                this.tokens.push({
                    type: "sequence",
                    format,
                    sequenceKey,
                    context,
                    initialValue
                });
                currentPos = endPos + 1;
            } else if (char === ":") {
                this.tokens.push({
                    type: "separator",
                    value: char,
                });
                currentPos++;
            } else {
                this.tokens.push({
                    type: "literal",
                    value: char,
                });
                currentPos++;
            }
        }
    }

    validateInitialValue(format, initialValue) {
        if (!initialValue) return true;
        if (format.length !== initialValue.length) {
            throw new Error(`Initial value length (${initialValue.length}) does not match format length (${format.length})`);
        }

        for (let i = 0; i < format.length; i++) {
            const formatChar = format[i].toLowerCase();
            const valueChar = initialValue[i];

            if (formatChar === 'c') {
                if (!/^[A-Z]$/i.test(valueChar)) {
                    throw new Error(`Invalid character '${valueChar}' for format 'c' at position ${i}`);
                }
            } else if (formatChar === 'n') {
                if (!/^[0-9]$/.test(valueChar)) {
                    throw new Error(`Invalid number '${valueChar}' for format 'n' at position ${i}`);
                }
            }
        }
        return true;
    }

    calculateMaxCombinations(format) {
        return format.split("").reduce((total, char) => {
            if (char.toLowerCase() === "c") return total * 26; // A-Z
            if (char.toLowerCase() === "n") return total * 10; // 0-9
            return total;
        }, 1);
    }

    initializeSequence(key, format, context = [], initialValue = null) {
        if (!this.sequences.has(key)) {
            this.validateInitialValue(format, initialValue);

            const state = {
                format,
                context,
                maxValues: format.split("").map((char) =>
                    char.toLowerCase() === "c" ? "Z" : "9"
                ),
                maxCombinations: this.calculateMaxCombinations(format),
                initialValue
            };
            this.sequences.set(key, state);
        }
    }

    getOrCreateContextState(sequenceKey, contextKey) {
        const mapKey = `${sequenceKey}|${contextKey}`;
        if (!this.contextStates.has(mapKey)) {
            const state = this.sequences.get(sequenceKey);
            const initialChars = state.initialValue ?
                state.initialValue.split("") :
                state.format.split("").map((char) =>
                    char.toLowerCase() === "c" ? "A" : "0"
                );

            this.contextStates.set(mapKey, {
                chars: initialChars,
                currentCombination: 0
            });
        }
        return this.contextStates.get(mapKey);
    }

    shouldResetSequence(state, metadata) {
        if (!state.context.length || !state.lastContext) return false;

        // Check if any context values have changed
        return state.context.some(contextKey =>
            !state.lastContext ||
            state.lastContext[contextKey] !== metadata[contextKey]
        );
    }

    updateSequenceContext(state, metadata) {
        // Store current context values for next comparison
        state.lastContext = state.context.reduce((ctx, key) => {
            ctx[key] = metadata[key];
            return ctx;
        }, {});
    }

    extendSequence(state, contextState) {
        const lastChar = state.format[state.format.length - 1];
        state.format += lastChar;

        contextState.chars = state.format
            .split("")
            .map((char) => (char.toLowerCase() === "c" ? "A" : "0"));

        state.maxValues = state.format
            .split("")
            .map((char) => (char.toLowerCase() === "c" ? "Z" : "9"));

        state.maxCombinations = this.calculateMaxCombinations(state.format);
        contextState.currentCombination = 1;

        return contextState.chars.join("");
    }

    incrementSequence(sequenceKey, contextKey) {
        const state = this.sequences.get(sequenceKey);
        const contextState = this.getOrCreateContextState(sequenceKey, contextKey);
        const { chars } = contextState;
        const { maxValues } = state;

        contextState.currentCombination++;

        if (contextState.currentCombination >= state.maxCombinations) {
            switch (this.options.overflowStrategy) {
                case "error":
                    throw new SequenceOverflowError(state.format, state.maxCombinations);

                case "wrap":
                    contextState.currentCombination = 1;
                    contextState.chars = state.format
                        .split("")
                        .map((char) => (char.toLowerCase() === "c" ? "A" : "0"));
                    return contextState.chars.join("");

                case "extend":
                    return this.extendSequence(state, contextState);
            }
        }

        // Start from rightmost position
        for (let i = chars.length - 1; i >= 0; i--) {
            const currentChar = chars[i];
            const maxChar = maxValues[i];

            if (currentChar === maxChar) {
                chars[i] = maxChar === "9" ? "0" : "A";
            } else {
                chars[i] = String.fromCharCode(currentChar.charCodeAt(0) + 1);
                break;
            }
        }

        return chars.join("");
    }


    generate(metadata = {}) {
        const parts = this.tokens.map((token) => {
            switch (token.type) {
                case "metadata":
                    return metadata[token.key] || "NA";
                case "fixed":
                    return token.value;
                case "sequence":
                    return this.generateSequenceValue(token.sequenceKey, metadata);
                case "separator":
                case "literal":
                    return token.value;
                default:
                    throw new Error(`Unknown token type: ${token.type}`);
            }
        });

        return parts.join("");
    }

    generateSequenceValue(sequenceKey, metadata = {}) {
        const state = this.sequences.get(sequenceKey);
        const contextKey = this.getContextKey(sequenceKey, metadata);
        const contextState = this.getOrCreateContextState(sequenceKey, contextKey);

        const currentValue = contextState.chars.join("");
        this.incrementSequence(sequenceKey, contextKey);
        return currentValue;
    }

    resetSequence(state) {
        state.chars = state.format.split("").map((char) =>
            char.toLowerCase() === "c" ? "A" : "0"
        );
        state.currentCombination = 0;
    }


    getRemainingCombinations(sequenceKey) {
        const state = this.sequences.get(sequenceKey);
        return state.maxCombinations - state.currentCombination;
    }

    getMaxCombinations(sequenceKey) {
        const state = this.sequences.get(sequenceKey);
        return state.maxCombinations;
    }

    reset(contextFilter = null) {
        if (contextFilter) {
            // Reset specific context
            for (const [key, state] of this.contextStates) {
                if (key.includes(contextFilter)) {
                    const sequenceKey = key.split('|')[0];
                    const baseState = this.sequences.get(sequenceKey);
                    state.chars = baseState.format
                        .split("")
                        .map((char) => (char.toLowerCase() === "c" ? "A" : "0"));
                    state.currentCombination = 0;
                }
            }
        } else {
            // Reset all
            this.contextStates.clear();
        }
    }

    getContextStates() {
        const states = {};
        for (const [key, value] of this.contextStates.entries()) {
            states[key] = {
                sequence: value.chars.join(''),
                combination: value.currentCombination
            };
        }
        return states;
    }
}
